import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import i18n from '../../localization/i18n';
import * as rk from '../../localization/resourceKeys';
import styles from './myVWStatusDisplay.module.scss';
import { MyVWMarketingBanner } from '../components/common/banner/MyVWMarketingBanner';
import Button from '../components/common/button/button';
import Logger from '../../logger';
import { SessionContext } from '../../context/sessionContext';
import { useMyVW } from '../../context/myVWContext';
import warningIcon from '../assets/icons/red-icon.svg';
import checkmarkIcon from '../assets/icons/green-icon.svg';
import InlineSpinner from '../components/common/loader/inlineSpinner';
import BannerNotification from '../components/common/banner/bannerNotification';
import { unlinkAccount } from '../services/unlinkAccount';
import {
  MYVW_LINK_MY_ACCOUNT_BUTTON_CLICKED,
  MYVW_UNLINK_MY_ACCOUNT_BUTTON_CLICKED,
  MYVW_VIEW_MY_DASHBOARD_BUTTON_CLICKED,
} from '../../constants/BusinessSplunkEvent';

interface MyVWStatusDisplayProps {
  isLinked: boolean;
  originalSearch: string;
  openInNewTab?: boolean;
  vwfsToken?: string;
  onUnlink?: () => void;
}

const MyVWStatusDisplay: React.FC<MyVWStatusDisplayProps> = ({
  isLinked,
  originalSearch,
  vwfsToken,
  openInNewTab = true,
  onUnlink,
}) => {
  const sessionContext = useContext(SessionContext);
  const navigate = useNavigate();
  const { setIsLinked, partyId } = useMyVW();
  const [unlinkPending, setUnlinkPending] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);

  if (!sessionContext) return null;

  const { sessionDetails } = sessionContext;

  const buttonText = isLinked
    ? i18n.t(rk.BUTTON_MYVW_VIEW_DASHBOARD)
    : i18n.t(rk.BUTTON_MYVW_LINK_ACCOUNT);

  const statusText = isLinked
    ? i18n.t(rk.TEXT_MYVW_ACCOUNT_LINKED)
    : i18n.t(rk.TEXT_MYVW_ACCOUNT_NOT_LINKED);

  const navigateToTarget = (targetPath: string) => {
    if (openInNewTab) {
      window.open(targetPath, '_blank');
      return;
    }

    navigate(targetPath);
  };

  const handleUnlinkClick = () => {
    setUnlinkError(null);
    setShowUnlinkModal(true);
  };

  const handleCancelUnlink = () => {
    setShowUnlinkModal(false);
  };

  const handleUnlink = async () => {
    if (unlinkPending) return;

    Logger.info(
      'myVW status display: unlink account confirmed',
      'Location ==> /myVW/status-display',
      sessionDetails,
    );
    Logger.info(
      MYVW_UNLINK_MY_ACCOUNT_BUTTON_CLICKED,
      '',
      sessionDetails,
    );

    setShowUnlinkModal(false);

    try {
      setUnlinkError(null);
      setUnlinkPending(true);

      const result = await unlinkAccount({
        partyId: Number(partyId ?? 0),
        vwfsToken: vwfsToken ?? '',
        sessionDetails,
      });

      if (result.data?.isSuccess) {
        setIsLinked(false);
        onUnlink?.();
      } else if (result.data?.inMaintenance) {
        navigate('/myVW/login');
      } else {
        Logger.error(
          'myVW status display: unlink account failed',
          'Location ==> /myVW/status-display',
          result.data?.error || result.error,
          sessionDetails,
        );
        setUnlinkError(i18n.t(rk.ERROR_API_SERVICE_DOWN));
      }
    } catch (error) {
      Logger.error(
        'myVW status display: unlink account exception',
        'Location ==> /myVW/status-display',
        error as string,
        sessionDetails,
      );
      setUnlinkError(i18n.t(rk.ERROR_API_SERVICE_DOWN));
    } finally {
      setUnlinkPending(false);
    }
  };


  const handleAction = () => {
    if (isLinked) {
      Logger.info(
        'myVW status display: clicked view my dashboard button',
        'Location ==> /myVW/status-display',
        sessionDetails,
      );
      Logger.info(
        MYVW_VIEW_MY_DASHBOARD_BUTTON_CLICKED,
        '',
        sessionDetails,
      );

      const mfaParams = new URLSearchParams(originalSearch);
      mfaParams.set('isLinked', 'true');
      navigateToTarget(`/myVW/mfa?${mfaParams.toString()}`);
      return;
    }

    Logger.info(
      MYVW_LINK_MY_ACCOUNT_BUTTON_CLICKED,
      '',
      sessionDetails,
    );
    Logger.info(
      'myVW status display: clicked link my account button',
      'Location ==> /myVW/status-display',
      sessionDetails,
    );

    if (openInNewTab) {
      window.open(
        `/myVW/registration-question${originalSearch}`,
        '_blank',
      );
    } else {
      navigate(`/myVW/registration-question${originalSearch}`);
    }
  };

  return (
    <div className={`container ${styles.page} u-ph-small`}>
      <div className={styles.content}>
        {unlinkError && (
          <div className={styles.errorBanner}>
            <BannerNotification notificationStyle="error">
              {unlinkError}
            </BannerNotification>
          </div>
        )}

        <p className={styles.statusVerbiage}>
          {i18n.t(rk.TEXT_MYVW_STATUS_VERBIAGE)}
        </p>

        <div
          className={`${styles.statusCard} ${unlinkPending ? styles.statusCardPending : ''}`}
        >
          <div className={styles.statusRow}>
            {unlinkPending ? (
              <div className={styles.statusLoader}>
                <InlineSpinner label="Loading" color="primary" />
              </div>
            ) : isLinked ? (
              <img src={checkmarkIcon} alt="Success" />
            ) : (
              <img src={warningIcon} alt="Alert" />
            )}

            <span className={`${styles.statusText} ${unlinkPending ? styles.statusTextPending : ''}`}>
              {statusText}
            </span>
          </div>
        </div>

        {showUnlinkModal && (
          <div
            className={styles.unlinkConfirmationCard}
            data-testid="unlink-account-modal"
            role="dialog"
            aria-labelledby="unlink-confirmation-title"
            aria-describedby="unlink-confirmation-description"
          >
            <img src={warningIcon} alt="Alert" className={styles.unlinkWarningIcon} />

            <h2
              id="unlink-confirmation-title"
              className={styles.unlinkConfirmationTitle}
            >
              {i18n.t(rk.TITLE_MYVW_UNLINK_CONFIRMATION)}
            </h2>

            <p
              id="unlink-confirmation-description"
              className={styles.unlinkConfirmationText}
            >
              {i18n.t(rk.TEXT_MYVW_UNLINK_CONFIRMATION)}
            </p>

            <div className={styles.unlinkConfirmationActions}>
              <button
                type="button"
                className={styles.unlinkCancelButton}
                data-testid="cancelBtn"
                onClick={handleCancelUnlink}
                disabled={unlinkPending}
              >
                {i18n.t(rk.BUTTON_CANCEL)}
              </button>

              <button
                type="button"
                className={styles.unlinkConfirmButton}
                data-testid="continueBtn"
                onClick={handleUnlink}
                disabled={unlinkPending}
                aria-busy={unlinkPending}
              >
                {'Unlink'}
              </button>
            </div>
          </div>
        )}

        {!isLinked && <MyVWMarketingBanner styled />}

        <div className={styles.spacer} />

        <>
          <div className="btn-container">
            <Button
              testid="myvw-status-action-button"
              type="submit"
              value={buttonText}
              onClick={handleAction}
              disabled={unlinkPending}
              customClass={`c-btn--full ${styles.myVWActionButton}`}
              secondary={false}
              id="myvw-status-action-button"
              wrapperCustomClass=""
            />
          </div>

          {isLinked && sessionDetails.country === 'USA' && (
            <div className={styles.unlinkContainer}>
              <button
                type="button"
                data-testid="myvw-unlink-button"
                className={styles.unlinkButton}
                onClick={handleUnlinkClick}
                disabled={showUnlinkModal || unlinkPending}
                aria-disabled={showUnlinkModal || unlinkPending}
              >
                {i18n.t(rk.BUTTON_MYVW_UNLINK_ACCOUNT)}
              </button>
            </div>
          )}
        </>
      </div>
    </div>
  );
};

export default MyVWStatusDisplay;